import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
    useAddNotificationTemplateAdminMutation,
    useUpdateNotificationTemplateAdminMutation,
} from "../../../features/admin/notificationTemplate/NotificationTemplateApiAdmin";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";

export default function AddAdminNotificationTemplate() {
    const navigate = useNavigate();
    const location = useLocation();

    const [isUpdate, setIsUpdate] = useState(location?.state ? true : false);
    const [notificationType, setNotificationType] = useState(location?.state?.notification_type || "");
    const [title, setTitle] = useState(location?.state?.title || "");
    const [body, setBody] = useState(location?.state?.body || "");
    const [image, setImage] = useState(location?.state?.image || "");
    const [imageFile, setImageFile] = useState(null);
    const [type, setType] = useState(location?.state?.type || "FREQUENCY");
    const [subType, setSubType] = useState(location?.state?.sub_type || "");
    const [tenantId, setTenantId] = useState(location?.state?.tenant_id || "");
    const [vendorsList, setVendorsList] = useState([]);

    const [
        addNotificationTemplate,
        {
            data: addData,
            isLoading: addIsLoading,
            error: addError,
        },
    ] = useAddNotificationTemplateAdminMutation();

    const [
        updateNotificationTemplate,
        {
            data: updateData,
            isLoading: updateIsLoading,
            error: updateError,
        },
    ] = useUpdateNotificationTemplateAdminMutation();

    const [
        uploadImage,
        {
            data: uploadImageData,
            isLoading: uploadImageIsLoading,
            error: uploadImageError,
        },
    ] = useUploadSingleFileMutation();

    const {
        data: vendorsData,
        isError: vendorsIsError,
        isLoading: vendorsIsLoading,
    } = useFetchVendorsQuery(sessionStorage.getItem("access_token")) || {};

    useEffect(() => {
        if (vendorsData && vendorsData.body) {
            setVendorsList(vendorsData.body);
        }
    }, [vendorsData]);

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validation
        if (!notificationType || notificationType.trim() === "") {
            toast.error("Please enter notification type");
            return;
        }
        if (!title || title.trim() === "") {
            toast.error("Please enter title");
            return;
        }
        if (!body || body.trim() === "") {
            toast.error("Please enter body");
            return;
        }

        // If there's a file to upload, upload it first
        if (imageFile) {
            const formData = new FormData();
            formData.append("image", imageFile);
            uploadImage({
                body: formData,
            });
        } else {
            // No file to upload, proceed with submission
            submitTemplate(image);
        }
    };

    const submitTemplate = (imageUrl) => {
        // Validation for tenant
        if (!tenantId || tenantId === "") {
            toast.error("Please select a tenant");
            return;
        }

        const payload = {
            notification_type: notificationType.trim(),
            title: title.trim(),
            body: body.trim(),
            image: imageUrl || "",
            type: type,
            sub_type: type === "FREQUENCY" && subType ? subType : null,
            tenant_id: tenantId,
        };

        if (isUpdate) {
            updateNotificationTemplate({
                token: sessionStorage.getItem("access_token"),
                id: location.state.id || location.state.id,
                body: { ...payload, tenant_id: tenantId },
            });
        } else {
            addNotificationTemplate({
                token: sessionStorage.getItem("access_token"),
                body: payload,
            });
        }
    };

    useEffect(() => {
        if (addData) {
            toast.dismiss();
            toast.success("Notification template added successfully");
            navigate(-1);
        }
        if (addError) {
            toast.dismiss();
            toast.error(addError?.data?.message || "Failed to add notification template");
        }
    }, [addData, addError]);

    useEffect(() => {
        if (updateData) {
            toast.dismiss();
            toast.success("Notification template updated successfully");
            navigate(-1);
        }
        if (updateError) {
            toast.dismiss();
            toast.error(updateError?.data?.message || "Failed to update notification template");
        }
    }, [updateData, updateError]);

    useEffect(() => {
        if (uploadImageData) {
            toast.dismiss();
            const imageUrl = uploadImageData.body.fileLink;
            setImage(imageUrl);
            submitTemplate(imageUrl);
        }
        if (uploadImageError) {
            toast.dismiss();
            toast.error("Image upload failed");
        }
        if (uploadImageIsLoading) {
            toast.loading("Uploading image...", { id: "uploadImageLoading" });
        }
    }, [uploadImageData, uploadImageError, uploadImageIsLoading]);

    return (
        <div className="">
            <div className="page-content">
                <div className="card p-4">
                    <h4 className="mb-3 card-title">
                        {isUpdate ? "Update" : "Add"} Notification Template
                    </h4>
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-12 mb-3">
                                <label className="form-label">
                                    Select Tenant <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-control"
                                    value={tenantId}
                                    onChange={(e) => setTenantId(e.target.value)}
                                >
                                    <option value="">Select Tenant...</option>
                                    {vendorsList.map((vendor) => (
                                        <option key={vendor.tenant_id} value={vendor.tenant_id}>
                                            {vendor.name.charAt(0).toUpperCase() + vendor.name.slice(1)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    Notification Type <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter notification type"
                                    value={notificationType}
                                    onChange={(e) => setNotificationType(e.target.value)}
                                />
                            </div>

                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    Type <span className="text-danger">*</span>
                                </label>
                                <select
                                    className="form-control"
                                    value={type}
                                    onChange={(e) => {
                                        setType(e.target.value);
                                        // Reset sub type if switching to EVENT
                                        if (e.target.value === "EVENT") {
                                            setSubType("");
                                        }
                                    }}
                                >
                                    <option value="FREQUENCY">Frequency</option>
                                    <option value="EVENT">Event</option>
                                </select>
                            </div>

                            {type === "FREQUENCY" && (
                                <div className="col-md-12 mb-3">
                                    <label className="form-label">
                                        Sub Type
                                    </label>
                                    <select
                                        className="form-control"
                                        value={subType}
                                        onChange={(e) => setSubType(e.target.value)}
                                    >
                                        <option value="">Select Sub Type</option>
                                        <option value="ALL">All</option>
                                        <option value="CUSTOM">Custom</option>
                                    </select>
                                </div>
                            )}

                            <div className="col-md-12 mb-3">
                                <label className="form-label">
                                    Title <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </div>

                            <div className="col-md-12 mb-3">
                                <label className="form-label">
                                    Body <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    placeholder="Enter body content"
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                ></textarea>
                            </div>

                            <div className="col-md-12 mb-3">
                                <label className="form-label">Image</label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept="image/*"
                                    onChange={(e) => setImageFile(e.target.files[0])}
                                />
                                {image && !imageFile && (
                                    <div className="mt-2">
                                        <small className="text-muted">Current image: </small>
                                        <a href={image} target="_blank" rel="noopener noreferrer">
                                            View
                                        </a>
                                    </div>
                                )}
                            </div>

                            <div className="col-12">
                                <button
                                    type="submit"
                                    className="btn btn-primary me-2"
                                    disabled={addIsLoading || updateIsLoading || uploadImageIsLoading}
                                >
                                    {addIsLoading || updateIsLoading || uploadImageIsLoading
                                        ? "Saving..."
                                        : isUpdate
                                            ? "Update"
                                            : "Add"}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => navigate(-1)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
